// V1.0.35: Modal drill-down từ báo cáo → danh sách task chi tiết.
//
// LƯU Ý VỀ NGUỒN DỮ LIỆU:
//   ReportsController KHÔNG có endpoint drill-down riêng, nên modal này dùng
//   GET /api/tasks (taskService.list) rồi lọc lại phía client theo đúng công thức
//   của ReportsService.OverdueByDepartmentAsync:
//     - cửa sổ thời gian: lọc theo createdAt trong [fromDate, toDate]
//     - overdue     : status ∉ {CANCELLED, ARCHIVED} VÀ
//                     ((status ≠ COMPLETED VÀ dueDate < hôm nay)
//                      HOẶC (status = COMPLETED VÀ completedAt > dueDate))
//     - inProgress  : status ∈ {NEW, ASSIGNED, IN_PROGRESS}
//     - completed   : status = COMPLETED
//
//   Với user có quyền task:view_all, backend chỉ trả task ROOT (parent_task_id IS NULL)
//   nên số dòng ở đây có thể NHỎ HƠN con số tổng trong bảng báo cáo (vốn đếm cả subtask).
//   Nếu cần khớp tuyệt đối thì phải bổ sung endpoint drill-down ở ReportsController.
import { useEffect, useMemo, useState } from 'react';
import { Modal, Table, Tag, Empty, App as AntdApp } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { taskService } from '@/services/taskService';
import type { Task } from '@/types';

export interface TaskListPopupParams {
  visible: boolean;
  /** Tiêu đề modal, do trang báo cáo dựng sẵn (vd "Task quá hạn — Phòng CNTT") */
  title: string;
  /** Màu nhấn của bucket đang xem */
  color: string;
  /** Lọc theo khoa/phòng */
  departmentId?: string;
  /** Nhóm chỉ số được click trong báo cáo */
  statusBucket: 'all' | 'overdue' | 'inProgress' | 'completed';
  /** yyyy-MM-dd — cùng khoảng thời gian với báo cáo */
  fromDate?: string;
  toDate?: string;
  /** Tuỳ chọn: lọc thêm theo người thực hiện (báo cáo hiệu suất user) */
  userId?: string;
}

interface Props {
  params: TaskListPopupParams | null;
  onClose: () => void;
}

const STATUS_META: Record<string, { label: string; color: string }> = {
  NEW: { label: 'Mới', color: 'default' },
  ASSIGNED: { label: 'Đã giao', color: 'blue' },
  IN_PROGRESS: { label: 'Đang làm', color: 'processing' },
  PENDING_REVIEW: { label: 'Chờ duyệt', color: 'gold' },
  COMPLETED: { label: 'Hoàn thành', color: 'success' },
  CANCELLED: { label: 'Đã hủy', color: 'default' },
  OVERDUE: { label: 'Quá hạn', color: 'error' },
  ARCHIVED: { label: 'Lưu trữ', color: 'default' },
};

const PRIORITY_META: Record<string, { label: string; color: string }> = {
  LOW: { label: 'Thấp', color: 'default' },
  MEDIUM: { label: 'Trung bình', color: 'blue' },
  HIGH: { label: 'Cao', color: 'orange' },
  CRITICAL: { label: 'Khẩn cấp', color: 'red' },
};

/** Đúng công thức "quá hạn" của ReportsService (V1.0.61). */
function isOverdue(t: Task): boolean {
  if (t.status === 'CANCELLED' || t.status === 'ARCHIVED') return false;
  const due = dayjs(t.dueDate).startOf('day');
  if (t.status === 'COMPLETED') {
    return t.completedAt ? dayjs(t.completedAt).startOf('day').isAfter(due) : false;
  }
  return due.isBefore(dayjs().startOf('day'));
}

function matchBucket(t: Task, bucket: TaskListPopupParams['statusBucket']): boolean {
  switch (bucket) {
    case 'overdue':
      return isOverdue(t);
    case 'inProgress':
      return t.status === 'NEW' || t.status === 'ASSIGNED' || t.status === 'IN_PROGRESS';
    case 'completed':
      return t.status === 'COMPLETED';
    default:
      return true;
  }
}

export function TaskListPopupModal({ params, onClose }: Props) {
  const { message } = AntdApp.useApp();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<Task[]>([]);

  const open = !!params?.visible;

  useEffect(() => {
    if (!open || !params) {
      setRows([]);
      return;
    }
    let cancelled = false;

    (async () => {
      setLoading(true);
      try {
        const res = await taskService.list({
          departmentId: params.departmentId,
          assigneeId: params.userId,
          tab: 'all',
          page: 1,
          pageSize: 200,
        });
        if (cancelled) return;
        setRows(res.items ?? []);
      } catch (e: any) {
        if (cancelled) return;
        message.error(e?.response?.data?.message ?? e?.message ?? 'Không tải được danh sách task');
        setRows([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [open, params?.departmentId, params?.userId, params?.statusBucket, params?.fromDate, params?.toDate]);

  const data = useMemo(() => {
    if (!params) return [];
    const from = params.fromDate ? dayjs(params.fromDate).startOf('day') : null;
    const to = params.toDate ? dayjs(params.toDate).endOf('day') : null;

    return rows.filter(t => {
      const created = dayjs(t.createdAt);
      if (from && created.isBefore(from)) return false;
      if (to && created.isAfter(to)) return false;
      return matchBucket(t, params.statusBucket);
    });
  }, [rows, params]);

  const columns: ColumnsType<Task> = [
    {
      title: 'Mã',
      dataIndex: 'code',
      width: 110,
      render: (v: string) => <span className="mono" style={{ fontSize: 12 }}>{v}</span>,
    },
    {
      title: 'Tiêu đề',
      dataIndex: 'title',
      render: (v: string, r) => (
        <a onClick={() => { onClose(); navigate(`/tasks/${r.id}`); }}>{v}</a>
      ),
    },
    {
      title: 'Người thực hiện',
      width: 170,
      render: (_: unknown, r) => {
        const main = r.assignees?.find(a => a.role === 'MAIN') ?? r.assignees?.[0];
        return main
          ? <span style={{ fontSize: 12 }}>{main.user.fullName}</span>
          : <span style={{ color: '#adb6c0' }}>—</span>;
      },
    },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      width: 120,
      render: (v: string) => {
        const meta = STATUS_META[v] ?? { label: v, color: 'default' };
        return <Tag color={meta.color} style={{ marginInlineEnd: 0 }}>{meta.label}</Tag>;
      },
    },
    {
      title: 'Ưu tiên',
      dataIndex: 'priority',
      width: 110,
      render: (v: string) => {
        const meta = PRIORITY_META[v] ?? { label: v, color: 'default' };
        return <Tag color={meta.color} style={{ marginInlineEnd: 0 }}>{meta.label}</Tag>;
      },
    },
    {
      title: 'Hạn',
      dataIndex: 'dueDate',
      width: 110,
      render: (v: string, r) => (
        <span style={{ fontSize: 12, color: isOverdue(r) ? '#ff4d4f' : undefined }}>
          {v ? dayjs(v).format('DD/MM/YYYY') : '—'}
        </span>
      ),
    },
    {
      title: 'Tiến độ',
      dataIndex: 'progress',
      width: 80,
      align: 'right',
      render: (v: number) => <span style={{ fontSize: 12 }}>{v ?? 0}%</span>,
    },
  ];

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={1000}
      destroyOnClose
      title={
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <span style={{
            width: 8, height: 8, borderRadius: '50%',
            background: params?.color ?? '#1976D2', display: 'inline-block',
          }} />
          {params?.title ?? 'Danh sách task'}
          <span style={{ fontSize: 12, color: '#7d8a98', fontWeight: 400 }}>
            ({data.length})
          </span>
        </span>
      }
    >
      <Table<Task>
        rowKey="id"
        size="small"
        loading={loading}
        columns={columns}
        dataSource={data}
        locale={{ emptyText: <Empty description="Không có task nào" /> }}
        pagination={{
          pageSize: 20,
          showSizeChanger: true,
          pageSizeOptions: ['20', '50', '100'],
          showTotal: (t) => `${t} task`,
        }}
        scroll={{ y: 440 }}
      />
    </Modal>
  );
}

export default TaskListPopupModal;
